// src/lib/orchestrateur-navigation.js
//
// LE PASSAGE D'UN ÉCRAN À L'AUTRE, et le chemin du retour.
//
// ⚠️ LE DÉFAUT QUE CE MODULE CORRIGE. Les trois écrans de l'orchestrateur — le
// mois, la frise, les demandes d'horaires — ne se parlaient pas. On voyait un
// problème dans l'un, et il fallait changer d'onglet PUIS re-naviguer à la main
// jusqu'au bon jour ou à la bonne demande. Trois écrans qui montrent la même
// journée sans jamais pouvoir s'y renvoyer se lisent comme trois applications.
//
// ⚠️ ET SURTOUT : LE RETOUR. Un lien qui emmène sans ramener enferme. Chaque
// passage emporte donc SON chemin de retour — d'où il vient, et sur quoi
// rouvrir. C'est la seule chose que ce module fabrique.
//
// Helpers PURS : aucun DOM, aucun état React, aucun accès réseau. `App.jsx` en
// garde une valeur dans son état et la passe aux trois vues.

export const ONGLET_MOIS = 'menage-calendrier'
export const ONGLET_FRISE = 'menage-suivi'
export const ONGLET_HORAIRES = 'orchestrateur-horaires'

/**
 * Une destination : l'onglet visé, ce sur quoi l'ouvrir, et d'où l'on vient.
 *
 * `retour` est une destination elle aussi — jamais un simple nom d'onglet :
 * revenir « au mois » sans dire QUEL JOUR y rouvrir ramènerait sur une grille
 * fermée, et il faudrait re-chercher la case qu'on venait de quitter.
 *
 * @typedef {{onglet:string, jour?:string, demandeId?:string, retour?:object|null}} Destination
 */

function dest(onglet, champs = {}, retour = null) {
  const d = { onglet, retour: retour || null }
  if (champs.jour) d.jour = champs.jour
  if (champs.demandeId) d.demandeId = champs.demandeId
  return d
}

/** LIEN 1 — le mois envoie vers la frise, positionnée sur CE jour-là. */
export function versLaFrise(jour, retour = null) {
  if (!jour) return null
  return dest(ONGLET_FRISE, { jour }, retour)
}

/** Le retour vers le mois rouvre le jour qu'on venait de quitter, déplié. */
export function versLeMois(jour) {
  return dest(ONGLET_MOIS, { jour })
}

/**
 * LIEN 2 — la frise envoie vers UNE demande précise, pas vers la liste.
 *
 * Ouvrir la liste obligerait à retrouver, parmi N cartes, celle qu'on vient de
 * toucher — c'est-à-dire à refaire le travail que le clic venait d'épargner.
 */
export function versLaDemande(demandeId, jour, retour = null) {
  if (!demandeId) return null
  return dest(ONGLET_HORAIRES, { demandeId, jour }, retour)
}

/**
 * Le chemin du retour d'une destination — ou `null` s'il n'y en a pas.
 *
 * ⚠️ LE CHEMIN DE RETOUR PORTE LE SIEN — et ce n'est pas une pile.
 *
 * Mesuré à la traversée réelle des trois écrans (2026-08-11) : mois → frise →
 * demande → « voir la journée » ramenait bien à la frise, mais une frise SANS
 * flèche vers le mois. On était rentré chez soi par la fenêtre. La destination
 * « frise » qu'on confie à l'écran des demandes emporte donc le retour que la
 * frise avait elle-même — le chemin sait d'où il vient, sans qu'on empile quoi
 * que ce soit ni qu'on garde un historique.
 */
export function cheminRetour(destination) {
  return destination?.retour || null
}

/** Le libellé du bouton de retour — il NOMME la destination, jamais « Retour ». */
export function libelleRetour(destination) {
  const r = cheminRetour(destination)
  if (!r) return null
  if (r.onglet === ONGLET_MOIS) return 'Retour au mois'
  if (r.onglet === ONGLET_FRISE) return 'Retour à la journée'
  if (r.onglet === ONGLET_HORAIRES) return 'Retour aux demandes'
  return 'Retour'
}

/**
 * Ce qu'une vue doit consommer de la destination courante.
 *
 * ⚠️ ON NE CONSOMME QU'UNE FOIS. Sans ça, revenir sur l'onglet plus tard
 * rouvrirait le jour d'il y a une heure, comme si l'écran gardait rancune. La
 * vue appelle `consommer` au montage, applique ce qu'elle reçoit, et la
 * destination est effacée.
 */
export function pourOnglet(destination, onglet) {
  if (!destination || destination.onglet !== onglet) return null
  return destination
}
